import {
    CommercePlatformCartServiceControllersV1ValidatorsCartStatusCodeResult,
    CommercePlatformCartServiceModelsCartStatusCode
} from "../../api/openApi/cartService";

export class CartStatusCodeModel {
    private readonly _code: CommercePlatformCartServiceModelsCartStatusCode | undefined;
    private readonly _displayQuantity: number | undefined;
    private readonly _netQuantity: number | null | undefined;
    private readonly _sku: string | null | undefined;
    private readonly _productId: number | null | undefined;
    private readonly _artworkId: number | null | undefined;
    private readonly _width: number | null | undefined;
    private readonly _minimumWidth: number | null | undefined;
    private readonly _maximumWidth: number | null | undefined;
    private readonly _height: number | null | undefined;
    private readonly _minimumHeight: number | null | undefined;
    private readonly _maximumHeight: number | null | undefined;
    private readonly _isValidRequest: boolean | undefined;
    private readonly _serverMessage: string | undefined;

    constructor(cartStatusCode: CommercePlatformCartServiceControllersV1ValidatorsCartStatusCodeResult) {
        this._code = cartStatusCode.code;
        this._displayQuantity = cartStatusCode.displayQuantity;
        this._netQuantity = cartStatusCode.netQuantity
        this._sku = cartStatusCode.sku;
        this._productId = cartStatusCode.productId;
        this._artworkId = cartStatusCode.artworkId;
        this._width = cartStatusCode.width;
        this._minimumWidth = cartStatusCode.minimumWidth;
        this._maximumWidth = cartStatusCode.maximumWidth;
        this._height = cartStatusCode.height;
        this._minimumHeight = cartStatusCode.minimumHeight;
        this._maximumHeight = cartStatusCode.maximumHeight;
        this._isValidRequest = cartStatusCode.isValidRequest;
        this._serverMessage = cartStatusCode.message;
    }

    /**
     * @method code
     * @description Returns the cart validation code.
     * @returns {CommercePlatformCartServiceModelsCartStatusCode | undefined}
     */
    public get code(): CommercePlatformCartServiceModelsCartStatusCode | undefined {
        return this._code;
    }

    /**
     * @method displayQuantity
     * @description Returns the display quantity.
     * @returns {number | undefined}
     */
    public get displayQuantity(): number | undefined {
        return this._displayQuantity;
    }

    /**
     * @method netQuantity
     * @description Returns the net quantity.
     * @returns {number | null | undefined}
     */
    public get netQuantity(): number | null | undefined {
        return this._netQuantity;
    }

    /**
     * @method sku
     * @description Returns the sku.
     * @returns {string | null | undefined}
     */
    public get sku(): string | null | undefined {
        return this._sku;
    }

    /**
     * @method productId
     * @description Returns the product id.
     * @returns {number | null | undefined}
     */
    public get productId(): number | null | undefined {
        return this._productId;
    }

    /**
     * @method artworkId
     * @description Returns the artwork id.
     * @returns {number | null | undefined}
     */
    public get artworkId(): number | null | undefined {
        return this._artworkId;
    }

    /**
     * @method width
     * @description Returns the width.
     * @returns {number | null | undefined}
     */
    public get width(): number | null | undefined {
        return this._width;
    }

    /**
     * @method minimumWidth
     * @description Returns the minimum width.
     * @returns {number | null | undefined}
     */
    public get minimumWidth(): number | null | undefined {
        return this._minimumWidth;
    }

    /**
     * @method maximumWidth
     * @description Returns the maximum width.
     * @returns {number | null | undefined}
     */
    public get maximumWidth(): number | null | undefined {
        return this._maximumWidth;
    }

    /**
     * @method height
     * @description Returns the height.
     * @returns {number | null | undefined}
     */
    public get height(): number | null | undefined {
        return this._height;
    }

    /**
     * @method minimumHeight
     * @description Returns the minimum height.
     * @returns {number | null | undefined}
     */
    public get minimumHeight(): number | null | undefined {
        return this._minimumHeight;
    }

    /**
     * @method maximumHeight
     * @description Returns the maximum height.
     * @returns {number | null | undefined}
     */
    public get maximumHeight(): number | null | undefined {
        return this._maximumHeight;
    }

    /**
     * @method isValidRequest
     * @description Returns the validity of the request.
     * @returns {boolean | undefined}
     */
    public get isValidRequest(): boolean | undefined {
        return this._isValidRequest;
    }

    /**
     * @method serverMessage
     */
    public get serverMessage(): string | undefined {
        return this._serverMessage;
    }

    /**
     * @method message
     * @description Returns the display message.
     * @returns {string | undefined}
     */
    public get message(): string | undefined {
        const displayQuantity = this._displayQuantity ?? 0;
        const sku = this._sku ?? "";
        const minimumWidth = this._minimumWidth ?? "";
        const maximumWidth = this._maximumWidth ?? "";
        const minimumHeight = this._minimumHeight ?? "";
        const maximumHeight = this._maximumHeight ?? "";

        switch (this._code) {
            case "MaximumQuantityInTransit":
                return `We're sorry, but there ${(displayQuantity > 1 ? "are" : "is")} only ${displayQuantity} unit${(displayQuantity > 1 ? "s" : "")} of product '${sku}' available to back order. The maximum quantity available has been added to your cart.`
            case "DynamicDimensionsHeight":
                return `Height must be between ${minimumHeight} and ${maximumHeight} inches.`
            case "DynamicDimensionsWidth":
                return `Width must be between ${minimumWidth} and ${maximumWidth} inches.`
            case "HardMinimum":
                return `We're sorry, but the product '${sku}' has recently run out of stock and we do not accept backorders for this item. Please check back later.`
            case "BackOrder":
                return `We're sorry, but the product '${sku}' has recently run out of stock and we do not accept backorders for this item. Please check back later.`
            case "MinimumOrderQuantity":
                return `The quantity specified for '${sku}' is below the minimum order quantity for this product. Add at least ${displayQuantity} units and try again.`
            case "MaximumQuantityInWarehouse":
                return `We're sorry, but there ${(displayQuantity > 1 ? "are" : "is")} only ${displayQuantity} unit${(displayQuantity > 1 ? "s" : "")} of product '${sku}' available. The maximum quantity available has been added to your cart.`
            case "MinimumQuantity":
                return `We're sorry, but the minimum quantity of ${displayQuantity} has been added to your cart.`
        }

        return undefined;
    }

}